class PromotionalMaterialsProduct {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector("[data-promo-form]");
    this.moneyFormat = section.dataset.moneyFormat || "${{amount}}";
    this.usesJsonPricing = section.dataset.jsonPricing === "true";
    this.isSubmitting = false;
    this.isIndentedProduct = section.dataset.indentedProduct === "true";
    this.minimumQuantity = Math.max(
      1,
      Number(section.dataset.minimumQuantity) || 1,
    );
    this.indentDays = Number.parseInt(section.dataset.indentDays || "", 10);
    this.productConfiguration = null;
    const configurationElement = section.querySelector(
      "[data-product-configuration]",
    );
    if (configurationElement) {
      try {
        this.productConfiguration = JSON.parse(
          configurationElement.textContent,
        );
      } catch (error) {
        console.error("Invalid TRENDS product configuration JSON", error);
      }
    }
    this.setupCustomVariantOptions();
    this.setupIndentedProduct();
    this.bindGallery();
    this.bindReasonsSlider();
    this.bindSynchronizedScroll();
    this.bindColours();
    this.bindOrderTypes();
    this.bindAddons();
    this.bindBrandingPricingTables();
    this.bindQuantity();
    this.bindStockLevels();
    this.bindSelectionEdit();
    this.bindForm();
    this.bindStickyAddButton();
    this.paintSwatches();
    this.moveAfterpayMessage();
    this.applyAfterpayStyles();
    this.update();
  }

  setupIndentedProduct() {
    if (!this.isIndentedProduct) return;

    const brandingOrderType = this.section.querySelector(
      '[data-order-type="2"]',
    );
    if (brandingOrderType) brandingOrderType.checked = true;

    const orderTypes = this.section.querySelector(
      ".promotional-product__order-types",
    );
    if (orderTypes) orderTypes.hidden = true;

    const brandingOptions = this.section.querySelector(
      "[data-branding-options]",
    );
    if (brandingOptions) brandingOptions.hidden = false;

    const brandingGuidance = this.section.querySelector(
      "[data-branding-guidance]",
    );
    if (brandingGuidance) brandingGuidance.hidden = false;

    this.selectFirstBrandingOption();
  }

  moveAfterpayMessage() {
    const target = this.section.querySelector("[data-promo-afterpay-target]");
    if (!target) return;

    const afterpayBlock = [
      ...this.section.querySelectorAll(".shopify-app-block"),
    ].find((block) => block.querySelector("square-placement"));
    if (afterpayBlock) target.appendChild(afterpayBlock);
  }

  applyAfterpayStyles() {
    const injectStyles = () => {
      this.section.querySelectorAll("square-placement").forEach((placement) => {
        const root = placement.shadowRoot;
        if (!root || root.querySelector("[data-promo-afterpay-styles]")) return;

        const style = document.createElement("style");
        style.setAttribute("data-promo-afterpay-styles", "");
        style.textContent = `
          .afterpay-main-text,
          .afterpay-main-text * {
            font-size: 12px !important;
            line-height: 14px !important;
            letter-spacing: 0 !important;
          }

          .afterpay-main-text {
            white-space: nowrap !important;
          }

          .afterpay-logo.type-badge,
          .afterpay-logo.type-badge svg,
          .afterpay-logo.logo-cashappafterpay,
          .afterpay-logo.logo-cashappafterpay svg {
            width: auto !important;
            height: 15px !important;
            max-height: 15px !important;
          }
        `;
        root.appendChild(style);
      });
    };

    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined("square-placement").then(injectStyles);
    }
    injectStyles();
    [250, 500, 1000, 2000].forEach((delay) =>
      window.setTimeout(injectStyles, delay),
    );
  }

  setupCustomVariantOptions() {
    const configuration =
      this.productConfiguration?.custom_variant_configuration;
    const customVariantsEnabled =
      configuration?.enabled === true || configuration?.enabled === "true";
    if (
      !customVariantsEnabled ||
      !Array.isArray(configuration.options) ||
      !Array.isArray(configuration.variants)
    )
      return;

    const colourList = this.section.querySelector("[data-promo-colours]");
    const colourFieldset = colourList?.closest("fieldset");
    const orderTypes = this.section.querySelector(
      ".promotional-product__order-types",
    );
    const quantity = this.section.querySelector(
      ".promotional-product__quantity",
    );
    if (!colourList || !colourFieldset || !orderTypes || !quantity) return;

    const variantDataBySku = new Map();
    let initiallySelectedSku = "";
    colourList.querySelectorAll('input[name="Colour"]').forEach((input) => {
      const sku = String(input.dataset.variantSku || "");
      if (sku) variantDataBySku.set(sku, { ...input.dataset });
      if (input.checked) initiallySelectedSku = sku;
    });
    const shopifyVariantsElement = this.section.querySelector(
      "[data-promo-shopify-variants]",
    );
    if (shopifyVariantsElement) {
      try {
        const shopifyVariants = JSON.parse(
          shopifyVariantsElement.textContent || "[]",
        );
        shopifyVariants.forEach((variant) => {
          const sku = String(variant.sku || "");
          if (!sku) return;
          const existing = variantDataBySku.get(sku) || {};
          const featuredImage = variant.featured_image || {};
          const featuredMedia = variant.featured_media || {};
          variantDataBySku.set(sku, {
            ...existing,
            variantId: String(variant.id || existing.variantId || ""),
            variantSku: sku,
            mediaId: String(
              featuredMedia.id || featuredImage.id || existing.mediaId || "",
            ),
            selectionImageUrl:
              featuredImage.src ||
              featuredMedia.preview_image?.src ||
              existing.selectionImageUrl ||
              "",
            price: String(variant.price ?? existing.price ?? ""),
          });
        });
      } catch (error) {
        console.error("Invalid Shopify promotional variant JSON", error);
      }
    }

    const primaryOption =
      configuration.options.find(
        (option) =>
          String(option.key).toLowerCase() ===
          String(configuration.primaryOptionKey).toLowerCase(),
      ) || configuration.options[0];
    let initialVariant =
      configuration.variants.find(
        (variant) => String(variant.sku) === initiallySelectedSku,
      ) || configuration.variants[0];
    const initialVariantIsComplete = configuration.options.every(
      (option) => initialVariant?.attributes?.[option.name] != null,
    );
    if (!initialVariantIsComplete) {
      initialVariant =
        configuration.variants.find((variant) =>
          configuration.options.every(
            (option) => variant.attributes?.[option.name] != null,
          ),
        ) || initialVariant;
    }
    const moreButton = colourList.querySelector("[data-more-colours]");

    this.customVariantConfiguration = configuration;
    this.customVariantDataBySku = variantDataBySku;
    this.customPrimaryOption = primaryOption;
    this.customOptionCount = configuration.options.length;

    const configure = this.section.querySelector(
      ".promotional-product__configure",
    );
    const configuredPrefix = String(
      configure?.dataset.optionLabelPrefix || "Choose your",
    ).trim();
    const primaryNamePattern = new RegExp(
      `\\s+${String(primaryOption.name).replace(/[.*+?^${}()|[\\]\\]/g, "\\$&")}$`,
      "i",
    );
    this.customOptionLabelPrefix =
      configuredPrefix.replace(primaryNamePattern, "").trim() || "Choose your";
    const optionHeading = (option, index) =>
      `${index + 1}. ${this.customOptionLabelPrefix} ${String(option.name).toLowerCase()}`;

    const legend = colourFieldset.querySelector("legend");
    if (legend) legend.textContent = optionHeading(primaryOption, 0);
    colourList.replaceChildren();

    primaryOption.values.forEach((value, index) => {
      const label = document.createElement("label");
      label.className = "promotional-product__colour";
      label.dataset.colourIndex = String(index + 1);
      const input = document.createElement("input");
      input.type = "radio";
      input.name = primaryOption.name;
      input.value = value;
      input.dataset.promoConfigOption = primaryOption.key;
      input.dataset.promoPrimaryOption = "true";
      input.checked =
        initialVariant?.attributes?.[primaryOption.name] === value;
      const swatch = document.createElement("span");
      swatch.dataset.colourName = value;
      const text = document.createElement("small");
      text.textContent = primaryOption.displayValues?.[value] || value;
      label.append(input, swatch, text);
      colourList.append(label);
    });
    if (moreButton) colourList.append(moreButton);

    configuration.options
      .filter((option) => option !== primaryOption)
      .forEach((option) => {
        const optionIndex = configuration.options.indexOf(option);
        const fieldset = document.createElement("fieldset");
        fieldset.className = "promotional-product__variant-option";
        fieldset.dataset.promoConfigFieldset = option.key;
        const optionLegend = document.createElement("legend");
        optionLegend.textContent = optionHeading(option, optionIndex);
        const values = document.createElement("div");
        values.className = "promotional-product__variant-option-values";
        option.values.forEach((value) => {
          const label = document.createElement("label");
          const input = document.createElement("input");
          const text = document.createElement("span");
          input.type = "radio";
          input.name = `Promotional option ${option.key}`;
          input.value = value;
          input.dataset.promoConfigOption = option.key;
          input.checked = initialVariant?.attributes?.[option.name] === value;
          text.textContent = option.displayValues?.[value] || value;
          label.append(input, text);
          values.append(label);
        });
        fieldset.append(optionLegend, values);
        quantity.before(fieldset);
      });
    this.updateStepNumbers();

    this.section
      .querySelectorAll("[data-promo-config-option]")
      .forEach((input) => {
        input.addEventListener("change", () => {
          this.selectCompatibleCustomVariant(input);
          this.updateCustomOptionVisibility();
          this.syncCustomVariantSelection();
          if (this.selectedCustomVariant()) this.showError("");
          this.showVariantMedia(this.selectedColour());
          this.update();
        });
      });
    this.updateCustomOptionVisibility();
    this.syncCustomVariantSelection();
  }

  updateCustomOptionVisibility() {
    if (!this.customVariantConfiguration || !this.customPrimaryOption) return;
    const selectedPrimary = this.section.querySelector(
      `[data-promo-config-option="${CSS.escape(String(this.customPrimaryOption.key))}"]:checked`,
    );
    const matchingVariants = this.customVariantConfiguration.variants.filter(
      (variant) =>
        variant.attributes?.[this.customPrimaryOption.name] ===
        selectedPrimary?.value,
    );
    let visibleOptionCount = 1;

    this.customVariantConfiguration.options
      .filter((option) => option !== this.customPrimaryOption)
      .forEach((option) => {
        const fieldset = this.section.querySelector(
          `[data-promo-config-fieldset="${CSS.escape(String(option.key))}"]`,
        );
        const isApplicable = matchingVariants.some(
          (variant) => variant.attributes?.[option.name] != null,
        );
        if (fieldset) fieldset.hidden = !isApplicable;
        if (!isApplicable) {
          fieldset?.querySelectorAll("input:checked").forEach((input) => {
            input.checked = false;
          });
        } else {
          visibleOptionCount += 1;
        }
      });

    this.customOptionCount = visibleOptionCount;
    this.updateStepNumbers();
  }

  selectCompatibleCustomVariant(changedInput) {
    if (!this.customVariantConfiguration || !changedInput) return null;
    const changedOption = this.customVariantConfiguration.options.find(
      (option) =>
        String(option.key) === String(changedInput.dataset.promoConfigOption),
    );
    if (!changedOption) return null;

    const currentSelections = new Map(
      [
        ...this.section.querySelectorAll("[data-promo-config-option]:checked"),
      ].map((input) => [input.dataset.promoConfigOption, input.value]),
    );
    let candidates = this.customVariantConfiguration.variants.filter(
      (variant) =>
        variant.attributes?.[changedOption.name] === changedInput.value,
    );
    const completeCandidates = candidates.filter((variant) =>
      this.customVariantConfiguration.options.every(
        (option) => variant.attributes?.[option.name] != null,
      ),
    );
    if (completeCandidates.length) candidates = completeCandidates;
    if (!candidates.length) return null;

    const compatibleVariant =
      candidates.find((variant) =>
        this.customVariantConfiguration.options.every((option) => {
          if (option === changedOption) return true;
          const selectedValue = currentSelections.get(String(option.key));
          return (
            !selectedValue ||
            variant.attributes?.[option.name] === selectedValue
          );
        }),
      ) || candidates[0];

    this.customVariantConfiguration.options.forEach((option) => {
      const value = compatibleVariant.attributes?.[option.name];
      if (value == null) return;
      const matchingInput = [
        ...this.section.querySelectorAll("[data-promo-config-option]"),
      ].find(
        (input) =>
          String(input.dataset.promoConfigOption) === String(option.key) &&
          input.value === value,
      );
      if (matchingInput) matchingInput.checked = true;
    });
    return compatibleVariant;
  }

  selectedCustomVariant() {
    if (!this.customVariantConfiguration) return null;
    const selections = new Map(
      [
        ...this.section.querySelectorAll("[data-promo-config-option]:checked"),
      ].map((input) => [input.dataset.promoConfigOption, input.value]),
    );
    return (
      this.customVariantConfiguration.variants.find((variant) =>
        this.customVariantConfiguration.options.every((option) => {
          const variantValue = variant.attributes?.[option.name];
          return (
            variantValue == null ||
            variantValue === selections.get(String(option.key))
          );
        }),
      ) || null
    );
  }

  selectedStock() {
    const sku = String(
      this.selectedCustomVariant()?.sku ||
        this.selectedColour()?.dataset.variantSku ||
        "",
    );
    if (!sku || !Array.isArray(this.productConfiguration?.stock)) return null;
    const stock = this.productConfiguration.stock.find(
      (item) => String(item.stock_code ?? item.sku ?? "") === sku,
    );
    if (!stock) return null;
    return {
      sku,
      quantity: Math.max(0, Number(stock.quantity) || 0),
    };
  }

  syncCustomVariantSelection() {
    const variant = this.selectedCustomVariant();
    const primary = this.selectedColour();
    if (!variant || !primary) return null;
    const data = this.customVariantDataBySku?.get(String(variant.sku));
    primary.dataset.variantSku = String(variant.sku);
    if (data) Object.assign(primary.dataset, data);
    primary.dataset.variantSku = String(variant.sku);
    return variant;
  }

  updateStepNumbers() {
    const optionCount = this.customOptionCount || 1;
    const hiddenOrderTypeOffset = this.isIndentedProduct ? 1 : 0;
    this.section.querySelectorAll("[data-quantity-step]").forEach((step) => {
      step.textContent = `${optionCount + 1}.`;
    });
    this.section.querySelectorAll("[data-order-type-step]").forEach((step) => {
      step.textContent = `${optionCount + 2}.`;
    });
    this.section.querySelectorAll("[data-branding-step]").forEach((step) => {
      step.textContent = `${optionCount + 3 - hiddenOrderTypeOffset}.`;
    });
    this.section.querySelectorAll("[data-packaging-step]").forEach((step) => {
      step.textContent = `${optionCount + 4 - hiddenOrderTypeOffset}.`;
    });
  }

  bindStockLevels() {
    const triggers = [
      ...this.section.querySelectorAll("[data-stock-levels-open]"),
    ];
    const modal = this.section.querySelector("[data-stock-levels-modal]");
    if (!triggers.length || !modal) return;

    const loading = modal.querySelector("[data-stock-levels-loading]");
    const error = modal.querySelector("[data-stock-levels-error]");
    const content = modal.querySelector("[data-stock-levels-content]");
    const rows = modal.querySelector("[data-stock-levels-rows]");
    const formatter = new Intl.NumberFormat(
      document.documentElement.lang || "en-NZ",
    );
    let returnFocus = null;
    let requestController = null;
    let closeTimer = null;

    const setText = (selector, value) => {
      const target = modal.querySelector(selector);
      if (target) target.textContent = value;
    };

    const close = () => {
      if (modal.hidden) return;
      requestController?.abort();
      requestController = null;
      window.clearTimeout(closeTimer);
      modal.classList.remove("is-open");
      document.body.classList.remove("promotional-product-stock-open");
      closeTimer = window.setTimeout(() => {
        modal.hidden = true;
        returnFocus?.focus();
      }, 280);
    };

    const renderRows = (stock) => {
      rows.replaceChildren();
      stock.forEach((item) => {
        const row = document.createElement("tr");
        [
          item.description || item.colour || "—",
          formatter.format(Number(item.quantity) || 0),
          item.due_date || item.dueDate || "—",
        ].forEach((value) => {
          const cell = document.createElement("td");
          cell.textContent = value;
          row.appendChild(cell);
        });
        rows.appendChild(row);
      });
    };

    const load = async (trigger) => {
      requestController?.abort();
      requestController = new AbortController();
      loading.hidden = false;
      error.hidden = true;
      content.hidden = true;

      try {
        const response = await fetch(trigger.dataset.stockUrl, {
          headers: { Accept: "application/json" },
          signal: requestController.signal,
        });
        if (!response.ok)
          throw new Error(`Stock service returned ${response.status}.`);
        const data = await response.json();
        if (!data.ok || !Array.isArray(data.stock))
          throw new Error("Live stock data is currently unavailable.");

        renderRows(data.stock);
        loading.hidden = true;
        content.hidden = false;
        content.animate(
          [
            { opacity: 0, transform: "translateY(8px)" },
            { opacity: 1, transform: "translateY(0)" },
          ],
          { duration: 240, easing: "ease-out" },
        );
      } catch (fetchError) {
        if (fetchError.name === "AbortError") return;
        loading.hidden = true;
        error.textContent =
          "We couldn't load live stock levels right now. Please try again shortly.";
        error.hidden = false;
      }
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        window.clearTimeout(closeTimer);
        returnFocus = trigger;
        modal.hidden = false;
        document.body.classList.add("promotional-product-stock-open");
        window.requestAnimationFrame(() => {
          modal.classList.add("is-open");
          modal.querySelector("[data-stock-levels-close]")?.focus();
        });
        load(trigger);
      });
    });
    modal
      .querySelectorAll("[data-stock-levels-close]")
      .forEach((button) => button.addEventListener("click", close));
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) close();
    });
  }

  bindSynchronizedScroll() {
    const gallery = this.section.querySelector("[data-promo-gallery]");
    const purchase = this.section.querySelector(
      ".promotional-product__purchase",
    );
    const form = this.section.querySelector("[data-promo-form]");
    const quickBar = this.section.querySelector(
      ".promotional-product__quick-bar",
    );
    if (!gallery || !purchase || !form) return;

    let frame;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        gallery.style.removeProperty("--promo-sticky-top");
        purchase.style.removeProperty("--promo-sticky-top");

        if (!window.matchMedia("(min-width: 1200px)").matches) {
          gallery.classList.remove("is-promo-scroll-sticky");
          purchase.classList.remove("is-promo-scroll-sticky");
          return;
        }

        gallery.classList.add("is-promo-scroll-sticky");
        purchase.classList.add("is-promo-scroll-sticky");

        const gap = 20;
        const quickBarHeight = quickBar?.classList.contains("is-visible")
          ? quickBar.offsetHeight
          : 0;
        const preferredTop = quickBarHeight + gap;

        gallery.style.setProperty("--promo-sticky-top", `${preferredTop}px`);
        purchase.style.setProperty("--promo-sticky-top", `${preferredTop}px`);
      });
    };

    const observer = new ResizeObserver(update);
    observer.observe(gallery);
    observer.observe(purchase);
    observer.observe(form);
    if (quickBar) observer.observe(quickBar);
    window.addEventListener("resize", update, { passive: true });
    update();
  }

  bindStickyAddButton() {
    const stickyBar = this.section.querySelector("[data-promo-sticky-bar]");
    const mainButton = this.section.querySelector("[data-promo-main-submit]");
    if (!stickyBar || !mainButton) return;

    let wasVisible = false;
    const updateStickyBar = () => {
      const buttonRect = mainButton.getBoundingClientRect();
      const isMobile = window.matchMedia("(max-width: 989px)").matches;
      const shouldShow = mainButton.hidden
        ? false
        : isMobile
          ? window.scrollY > 300 &&
            (buttonRect.top > window.innerHeight || buttonRect.bottom < 0)
          : buttonRect.bottom < 0;

      stickyBar.classList.toggle("is-visible", shouldShow);
      stickyBar.setAttribute("aria-hidden", String(!shouldShow));
      stickyBar.toggleAttribute("inert", !shouldShow);
      const visibilityChanged = shouldShow !== wasVisible;
      wasVisible = shouldShow;
      if (visibilityChanged) window.dispatchEvent(new Event("resize"));
    };

    const observer = new IntersectionObserver(updateStickyBar, {
      threshold: 0.01,
    });
    observer.observe(mainButton);
    window.addEventListener("scroll", updateStickyBar, { passive: true });
    window.addEventListener("resize", updateStickyBar, { passive: true });
    updateStickyBar();
  }

  bindSelectionEdit() {
    const editButton = this.section.querySelector("[data-selection-edit]");
    const firstOption = this.section.querySelector(
      ".promotional-product__colours",
    );
    if (!editButton || !firstOption) return;

    editButton.addEventListener("click", () => {
      firstOption.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        const selectedInput = firstOption.querySelector(
          'input[type="radio"]:checked',
        );
        selectedInput?.focus({ preventScroll: true });
      }, 500);
    });
  }

  bindGallery() {
    if (typeof window.Splide === "undefined") return;
    const mainElement = this.section.querySelector("[data-promo-main-slider]");
    const thumbnailElement = this.section.querySelector(
      "[data-promo-thumbnail-slider]",
    );
    if (!mainElement || mainElement.dataset.splideInitialized === "true")
      return;
    const mediaSlides = [
      ...mainElement.querySelectorAll("[data-promo-media]"),
    ];
    const hasMultipleMedia = mediaSlides.length > 1;
    const selectedMediaId = this.selectedColour()?.dataset.mediaId;
    const initialSlideIndex = mediaSlides.findIndex(
      (slide) => slide.dataset.promoMedia === selectedMediaId,
    );

    const main = new window.Splide(mainElement, {
      type: "slide",
      perPage: 1,
      perMove: 1,
      speed: 550,
      rewind: hasMultipleMedia,
      pagination: false,
      arrows: hasMultipleMedia,
      drag: hasMultipleMedia,
      keyboard: "focused",
      start: initialSlideIndex >= 0 ? initialSlideIndex : 0,
      breakpoints: {
        749: {
          arrows: false,
          pagination: hasMultipleMedia,
        },
      },
    });

    if (thumbnailElement) {
      const thumbnails = new window.Splide(thumbnailElement, {
        type: "slide",
        start: initialSlideIndex >= 0 ? initialSlideIndex : 0,
        perPage: 3,
        perMove: 1,
        gap: 15,
        rewind: true,
        pagination: false,
        arrows: true,
        isNavigation: true,
        focus: 0,
        trimSpace: true,
        padding: { right: "70px" },
        drag: true,
        keyboard: "focused",
        breakpoints: {
          749: {
            perPage: 3,
            gap: 7,
            padding: { right: "42px" },
          },
        },
      });
      main.sync(thumbnails);
      thumbnailElement.dataset.splideInitialized = "true";
      this.thumbnailSplide = thumbnails;
      thumbnails.mount();
      main.mount();
      thumbnailElement
        .querySelectorAll("[data-promo-thumbnail]")
        .forEach((thumbnail, index) => {
          thumbnail.addEventListener("click", () => main.go(index));
        });
    } else {
      main.mount();
    }

    mainElement.dataset.splideInitialized = "true";
    this.mainSplide = main;
  }

  bindReasonsSlider() {
    const element = this.section.querySelector("[data-promo-reasons]");
    if (!element || typeof window.Splide === "undefined") return;

    const media = window.matchMedia("(max-width: 989px)");
    const update = () => {
      if (media.matches && !this.reasonsSplide) {
        this.reasonsSplide = new window.Splide(element, {
          type: "slide",
          perPage: 1,
          perMove: 1,
          gap: 12,
          arrows: false,
          pagination: true,
          rewind: true,
          drag: true,
          speed: 450,
        });
        this.reasonsSplide.mount();
      } else if (!media.matches && this.reasonsSplide) {
        this.reasonsSplide.destroy(true);
        this.reasonsSplide = null;
      }
    };

    media.addEventListener("change", update);
    update();
  }

  bindColours() {
    const colourList = this.section.querySelector("[data-promo-colours]");
    const moreButton = this.section.querySelector("[data-more-colours]");

    const visibleLimit = () => {
      if (!colourList) return 5;
      if (window.matchMedia("(min-width: 1441px)").matches) return 5;
      return Math.max(4, Math.floor((colourList.clientWidth + 12) / 70));
    };

    const renderColours = () => {
      if (!colourList) return;
      const colours = [...colourList.querySelectorAll("[data-colour-index]")];
      const expanded = colourList.dataset.expanded === "true";
      const columnCount = visibleLimit();
      const limit = Math.min(colours.length, columnCount);
      colourList.style.setProperty("--visible-colours", columnCount);

      colours.forEach((colour, index) => {
        colour.hidden = !expanded && index >= limit;
      });

      if (!moreButton) return;
      const remaining = Math.max(0, colours.length - limit);
      moreButton.hidden = remaining === 0;
      moreButton.setAttribute("aria-expanded", String(expanded));
      moreButton.innerHTML = expanded
        ? `<strong>−</strong><span>${moreButton.dataset.lessLabel}</span>`
        : `<strong>+${remaining}</strong><span>${moreButton.dataset.originalLabel}</span>`;
    };

    this.section.querySelectorAll('[name="Colour"]').forEach((input) =>
      input.addEventListener("change", () => {
        this.showVariantMedia(input);
        this.update();
      }),
    );
    moreButton?.addEventListener("click", () => {
      colourList.dataset.expanded = String(
        colourList.dataset.expanded !== "true",
      );
      renderColours();
    });

    window.addEventListener("resize", renderColours, { passive: true });
    renderColours();
  }

  showVariantMedia(colourInput) {
    const mediaId = colourInput?.dataset.mediaId;
    if (!this.mainSplide) return;
    if (!mediaId) {
      this.mainSplide.go(0);
      return;
    }
    const slides = [...this.section.querySelectorAll("[data-promo-media]")];
    const mediaIndex = slides.findIndex(
      (slide) => slide.dataset.promoMedia === mediaId,
    );
    if (mediaIndex >= 0) this.mainSplide.go(mediaIndex);
  }

  bindOrderTypes() {
    this.section.querySelectorAll("[data-order-type]").forEach((input) =>
      input.addEventListener("change", () => {
        const type = Number(input.dataset.orderType);
        const branding = this.section.querySelector("[data-branding-options]");
        const packaging = this.section.querySelector(
          "[data-packaging-options]",
        );
        const brandingGuidance = this.section.querySelector(
          "[data-branding-guidance]",
        );
        const brandingIsRequired = type === 2;
        if (branding) branding.hidden = !brandingIsRequired;
        if (brandingGuidance) brandingGuidance.hidden = !brandingIsRequired;
        if (brandingIsRequired) this.selectFirstBrandingOption();
        if (packaging)
          packaging.hidden = !brandingIsRequired || !this.giftReadyIsSelected();
        this.update();
      }),
    );

    this.section
      .querySelector("[data-gift-ready]")
      ?.addEventListener("change", (event) => {
        const packaging = this.section.querySelector(
          "[data-packaging-options]",
        );
        if (packaging) packaging.hidden = !event.currentTarget.checked;
        this.update();
      });
  }

  bindAddons() {
    this.section
      .querySelectorAll('[data-addon-id], [name="Packaging"]')
      .forEach((input) => {
        input.addEventListener("change", () => this.update());
      });
  }

  bindBrandingPricingTables() {
    const bulkToggle = this.section.querySelector("[data-bulk-pricing-toggle]");
    const bulkPricing = this.section.querySelector("[data-bulk-pricing]");
    bulkToggle?.addEventListener("click", () => {
      const expanded = bulkToggle.getAttribute("aria-expanded") === "true";
      bulkToggle.setAttribute("aria-expanded", String(!expanded));
      if (bulkPricing) bulkPricing.hidden = expanded;
    });

    this.section
      .querySelectorAll("[data-branding-pricing]")
      .forEach((pricingTable) => {
        const toggle = pricingTable.querySelector(
          "[data-branding-pricing-toggle]",
        );
        if (!toggle) return;

        const rows = [
          ...pricingTable.querySelectorAll("[data-branding-pricing-row]"),
        ];
        const initialCount = Number(pricingTable.dataset.initialCount || 2);

        toggle.addEventListener("click", () => {
          const expanded = toggle.getAttribute("aria-expanded") === "true";
          const nextExpanded = !expanded;

          rows.forEach((row) => {
            row.hidden =
              !nextExpanded &&
              Number(row.dataset.brandingPricingRow) > initialCount;
          });

          toggle.setAttribute("aria-expanded", String(nextExpanded));
          toggle.textContent = nextExpanded
            ? toggle.dataset.showLessLabel
            : toggle.dataset.showMoreLabel;
        });
      });
  }

  bindQuantity() {
    this.section.querySelectorAll('[name="Quantity preset"]').forEach((input) =>
      input.addEventListener("change", () => {
        const custom = this.section.querySelector("[data-custom-quantity]");
        const customTrigger = this.section.querySelector(
          "[data-custom-quantity-trigger]",
        );
        if (custom) {
          custom.hidden = input.value !== "custom";
          if (customTrigger) customTrigger.hidden = input.value === "custom";
          if (!custom.hidden) {
            custom.min = String(this.minimumQuantity);
            custom.focus();
          }
        }
        this.update();
      }),
    );
    this.section
      .querySelector("[data-custom-quantity]")
      ?.addEventListener("input", () => this.update());
  }

  quantity() {
    const selected = this.section.querySelector(
      '[name="Quantity preset"]:checked',
    );
    if (!selected) return 0;
    if (selected.value === "custom")
      return Math.max(
        0,
        Number(this.section.querySelector("[data-custom-quantity]")?.value) ||
          0,
      );
    return Number(selected.value) || 0;
  }

  selectedColour() {
    return this.section.querySelector(
      '[data-promo-primary-option]:checked, [name="Colour"]:checked',
    );
  }

  selectedOrderType() {
    return this.section.querySelector("[data-order-type]:checked");
  }

  selectedBranding() {
    const orderType = Number(this.selectedOrderType()?.dataset.orderType || 1);
    const brandingIsActive = orderType === 2;
    return brandingIsActive
      ? this.section.querySelector("[data-addon-id]:checked")
      : null;
  }

  selectedPackaging() {
    const orderType = Number(this.selectedOrderType()?.dataset.orderType || 1);
    return orderType === 2 && this.giftReadyIsSelected()
      ? this.section.querySelector('[name="Packaging"]:checked')
      : null;
  }

  giftReadyIsSelected() {
    return this.section.querySelector("[data-gift-ready]")?.checked === true;
  }

  selectedPackagingMethod() {
    const selected = this.selectedPackaging();
    if (!selected || !this.productConfiguration) return null;
    return (
      this.productConfiguration.packagingMethods?.find(
        (method) => String(method.id) === selected.dataset.packagingMethodId,
      ) || null
    );
  }

  giftUsesBranding() {
    return (
      this.section.querySelector("[data-gift-uses-branding]")?.dataset
        .giftUsesBranding === "true"
    );
  }

  selectFirstBrandingOption() {
    const selected = this.section.querySelector("[data-addon-id]:checked");
    const first = this.section.querySelector("[data-addon-id]");
    if (!selected && first) first.checked = true;
  }

  priceAtQuantity(prices, quantity) {
    if (!Array.isArray(prices) || !prices.length) return null;
    const sortedPrices = [...prices].sort(
      (first, second) => Number(first.quantity) - Number(second.quantity),
    );
    return sortedPrices.reduce(
      (selected, price) =>
        Number(price.quantity) <= quantity ? price : selected,
      sortedPrices[0],
    );
  }

  selectedBrandingMethod() {
    const selected = this.selectedBranding();
    if (!selected || !this.productConfiguration) return null;
    return (
      this.productConfiguration.brandingMethods?.find(
        (method) => String(method.id) === selected.dataset.brandingMethodId,
      ) || null
    );
  }

  pricingFor(quantity) {
    const baseTier = this.priceAtQuantity(
      this.productConfiguration?.quantityBreaks,
      quantity,
    );
    if (!baseTier) return null;

    const method = this.selectedBrandingMethod();
    const methodTier = method
      ? this.priceAtQuantity(method.quantityPrices, quantity)
      : null;
    const baseUnit = Math.round(Number(baseTier.unitPrice || 0) * 100);
    const brandingUnit = method
      ? Math.round(
          Number(
            methodTier?.brandingUnitPrice ??
              (methodTier?.combinedUnitPrice != null
                ? Number(methodTier.combinedUnitPrice) -
                  Number(baseTier.unitPrice || 0)
                : method.unitPrice) ??
              0,
          ) * 100,
        )
      : 0;
    const combinedUnit = baseUnit + Math.max(0, brandingUnit);
    const setupFee = method
      ? Math.round(Number(method.setupPrice || 0) * 100)
      : 0;
    const referenceTier = method
      ? method.quantityPrices?.[0]
      : this.productConfiguration.quantityBreaks?.[0];
    const referenceUnit = Math.round(
      Number(
        method ? referenceTier?.combinedUnitPrice : referenceTier?.unitPrice,
      ) * 100,
    );

    return {
      baseUnit,
      brandingUnit: Math.max(0, brandingUnit),
      combinedUnit,
      setupFee,
      referenceUnit,
    };
  }

  update() {
    this.syncCustomVariantSelection();
    const colour = this.selectedColour();
    const orderType = this.selectedOrderType();
    const branding = this.selectedBranding();
    const packaging = this.selectedPackaging();
    const quantity = this.quantity();
    const variantId = this.section.querySelector("[data-promo-variant-id]");
    if (variantId && colour) variantId.value = colour.dataset.variantId;

    this.setText("[data-selection-colour]", colour?.value || "—");
    const configuredVariant = this.selectedCustomVariant();
    const configuredValues = this.customVariantConfiguration?.options
      ?.map((option) => configuredVariant?.attributes?.[option.name])
      .filter(Boolean);
    this.setText(
      "[data-selection-colour-card]",
      configuredVariant?.fullName ||
        (configuredValues?.length
          ? configuredValues.join(" / ")
          : colour?.value || "—"),
    );
    this.setText(
      "[data-selection-option-label]",
      `${this.customPrimaryOption?.name || "Colour"}:`,
    );
    const selectionImage = this.section.querySelector(
      ".promotional-product__selection-image > img",
    );
    const selectionImageUrl = colour?.dataset.selectionImageUrl;
    if (selectionImage && selectionImageUrl) {
      selectionImage.removeAttribute("srcset");
      selectionImage.removeAttribute("sizes");
      selectionImage.src = selectionImageUrl;
    }
    const selectionSwatch = this.section.querySelector(
      "[data-selection-colour-swatch]",
    );
    const selectedSwatch = colour
      ?.closest("label")
      ?.querySelector("[data-colour-name]");
    if (selectionSwatch && selectedSwatch) {
      selectionSwatch.style.backgroundColor = getComputedStyle(selectedSwatch)
        .getPropertyValue("--swatch")
        .trim();
    }
    this.setText(
      "[data-selection-method]",
      branding?.value || orderType?.value || "—",
    );
    this.setText("[data-selection-quantity]", quantity || "—");
    this.updateOptionalRow(
      "[data-selection-packaging-row]",
      "[data-selection-packaging]",
      packaging?.value,
    );

    const tierPricing = this.pricingFor(quantity);
    const basePrice =
      tierPricing?.baseUnit ?? Number(colour?.dataset.price || 0);
    const brandingPrice = tierPricing
      ? tierPricing.brandingUnit
      : Number(branding?.dataset.addonPrice || 0);
    const packagingMethod = this.selectedPackagingMethod();
    const packagingTier = packagingMethod
      ? this.priceAtQuantity(packagingMethod.quantityPrices, quantity)
      : null;
    const packagingPrice = packagingMethod
      ? Math.round(
          Number(
            packagingTier?.packagingUnitPrice ?? packagingMethod.unitPrice ?? 0,
          ) * 100,
        )
      : Number(packaging?.dataset.addonPrice || 0);
    const setupFeePrice =
      tierPricing?.setupFee ?? Number(branding?.dataset.setupPrice || 0);
    const packagingSetupFeePrice = packagingMethod
      ? Math.round(Number(packagingMethod.setupPrice || 0) * 100)
      : 0;
    const combinedUnitPrice =
      (tierPricing?.combinedUnit ?? basePrice + brandingPrice) + packagingPrice;
    const fixedShipping = Math.round(
      Number(this.productConfiguration?.fixedShippingAmount || 0) * 100,
    );
    const markupPercent = Math.max(
      0,
      Number(this.productConfiguration?.markupPercent || 0),
    );
    const markupMultiplier = 1 + markupPercent / 100;
    const rawSubtotal =
      combinedUnitPrice * quantity +
      setupFeePrice +
      packagingSetupFeePrice +
      fixedShipping;
    const markupAmount = Math.round(rawSubtotal * (markupPercent / 100));
    const subtotal = rawSubtotal + markupAmount;
    const unitPriceExcludingGst =
      quantity > 0 ? Math.round(subtotal / quantity) : 0;
    const displayedBrandingPrice = Math.round(brandingPrice * markupMultiplier);
    const displayedPackagingPrice = Math.round(
      packagingPrice * markupMultiplier,
    );
    const displayedSetupFee = Math.round(
      (setupFeePrice + packagingSetupFeePrice) * markupMultiplier,
    );
    const displayedBaseUnitPrice =
      quantity > 0
        ? Math.max(
            0,
            Math.round(
              (subtotal -
                displayedBrandingPrice * quantity -
                displayedPackagingPrice * quantity -
                displayedSetupFee) /
                quantity,
            ),
          )
        : 0;
    this.setText(
      "[data-promo-unit-price]",
      this.formatMoney(unitPriceExcludingGst),
    );
    const save = this.section.querySelector("[data-promo-save]");
    const referenceUnit =
      (tierPricing?.referenceUnit || combinedUnitPrice) + packagingPrice;
    const savingPercentage =
      referenceUnit > combinedUnitPrice
        ? Math.round(
            ((referenceUnit - combinedUnitPrice) / referenceUnit) * 100,
          )
        : 0;
    if (save) {
      save.textContent = `Save ${savingPercentage}%`;
      save.hidden = savingPercentage <= 0;
    }
    this.setText(
      "[data-selection-unit-price]",
      this.formatMoney(displayedBaseUnitPrice),
    );
    this.setText(
      "[data-selection-addon-price]",
      this.formatMoney(displayedBrandingPrice),
    );
    const addonRow = this.section.querySelector("[data-selection-addon-row]");
    if (addonRow) addonRow.hidden = brandingPrice <= 0;
    this.setText(
      "[data-selection-packaging-price]",
      this.formatMoney(displayedPackagingPrice),
    );
    const packagingPriceRow = this.section.querySelector(
      "[data-selection-packaging-price-row]",
    );
    if (packagingPriceRow) packagingPriceRow.hidden = packagingPrice <= 0;
    this.section
      .querySelectorAll("[data-packaging-method-id]")
      .forEach((input) => {
        const method = this.productConfiguration?.packagingMethods?.find(
          (item) => String(item.id) === input.dataset.packagingMethodId,
        );
        const price = this.priceAtQuantity(method?.quantityPrices, quantity);
        const priceCents = Math.round(
          Number(price?.packagingUnitPrice ?? method?.unitPrice ?? 0) * 100,
        );
        input.dataset.addonPrice = String(priceCents);
        const target = input
          .closest("label")
          ?.querySelector("[data-packaging-option-price]");
        if (target) {
          const displayedPriceCents = Math.round(priceCents * markupMultiplier);
          target.textContent = input.hasAttribute("data-packaging-included")
            ? "Included"
            : `+${this.formatMoney(displayedPriceCents)} each`;
        }
      });
    this.setText(
      "[data-selection-setup-fee-price]",
      this.formatMoney(displayedSetupFee),
    );
    const setupFeeRow = this.section.querySelector(
      "[data-selection-setup-fee-row]",
    );
    if (setupFeeRow)
      setupFeeRow.hidden = setupFeePrice + packagingSetupFeePrice <= 0;
    this.setText("[data-selection-total]", this.formatMoney(subtotal));
    this.setText("[data-quick-total]", this.formatMoney(subtotal));
    this.section.querySelectorAll("square-placement").forEach((placement) => {
      placement.setAttribute(
        "data-amount",
        (subtotal / 100).toFixed(2),
      );
    });

    if (this.productConfiguration?.brandingMethods) {
      this.section
        .querySelectorAll("[data-branding-method-id]")
        .forEach((input) => {
          const method = this.productConfiguration.brandingMethods.find(
            (item) => String(item.id) === input.dataset.brandingMethodId,
          );
          const price = this.priceAtQuantity(method?.quantityPrices, quantity);
          const target = input
            .closest("label")
            ?.querySelector("[data-branding-option-price]");
          if (target && (price || method)) {
            const rawBrandingPrice = Math.round(
              Number(price?.brandingUnitPrice ?? method?.unitPrice ?? 0) * 100,
            );
            target.textContent = `${this.formatMoney(Math.round(rawBrandingPrice * markupMultiplier))} / unit`;
          }
        });
    }

    const quoteOnly = packaging?.hasAttribute("data-request-quote") || false;
    const selectedStock = this.selectedStock();
    const outOfStock =
      !this.isIndentedProduct &&
      selectedStock !== null &&
      selectedStock.quantity <= 0;
    const exceedsStock =
      !this.isIndentedProduct &&
      selectedStock !== null &&
      quantity > selectedStock.quantity;
    const customQuantity = this.section.querySelector("[data-custom-quantity]");
    const customQuantitySelected =
      this.section.querySelector('[name="Quantity preset"]:checked')?.value ===
      "custom";
    const belowMinimumQuantity =
      customQuantitySelected && quantity < this.minimumQuantity;
    if (customQuantity) {
      customQuantity.min = String(this.minimumQuantity);
      customQuantity.setCustomValidity(
        belowMinimumQuantity
          ? `The minimum order quantity is ${this.minimumQuantity.toLocaleString()}.`
          : "",
      );
      customQuantity.setAttribute(
        "aria-invalid",
        String(belowMinimumQuantity),
      );
      if (
        !this.isIndentedProduct &&
        selectedStock !== null &&
        selectedStock.quantity > 0
      ) {
        customQuantity.max = String(selectedStock.quantity);
      } else {
        customQuantity.removeAttribute("max");
      }
    }
    this.section
      .querySelectorAll('[name="Quantity preset"]')
      .forEach((input) => {
        const presetQuantity = Number(input.value);
        input.disabled =
          !this.isIndentedProduct &&
          selectedStock !== null &&
          !outOfStock &&
          input.value !== "custom" &&
          presetQuantity > selectedStock.quantity;
      });

    const currentError = this.section.querySelector("[data-promo-error]");
    if (outOfStock) {
      this.showError(
        currentError?.dataset.outOfStockMessage ||
          "The selected SKU is currently out of stock. Please Get a Quote to contact our team for further assistance.",
        "stock",
      );
    } else if (exceedsStock) {
      this.showError(
        `Only ${selectedStock.quantity.toLocaleString()} units are currently available for SKU ${selectedStock.sku}.`,
        "stock",
      );
    } else if (belowMinimumQuantity) {
      this.showError(
        `The minimum order quantity is ${this.minimumQuantity.toLocaleString()}.`,
        "quantity",
      );
    } else if (
      currentError?.dataset.errorSource === "stock" ||
      currentError?.dataset.errorSource === "quantity"
    ) {
      this.showError("");
    }
    this.section.querySelectorAll("[data-promo-submit]").forEach((button) => {
      button.disabled =
        quoteOnly || outOfStock || exceedsStock || belowMinimumQuantity;
      button.hidden = outOfStock;
    });
    this.section.classList.toggle("is-out-of-stock-quote", outOfStock);

    const quoteLink = this.section.querySelector("[data-promo-quote]");
    if (quoteLink) {
      const quoteUrl = new URL(quoteLink.href, window.location.origin);
      quoteUrl.searchParams.set("quantity", String(quantity));
      quoteLink.href = `${quoteUrl.pathname}${quoteUrl.search}${quoteUrl.hash}`;
    }

    this.updateStepNumbers();
  }

  updateOptionalRow(rowSelector, valueSelector, value) {
    const row = this.section.querySelector(rowSelector);
    if (row) row.hidden = !value;
    if (value) this.setText(valueSelector, value);
  }

  setText(selector, value) {
    const element = this.section.querySelector(selector);
    if (element) element.textContent = value;
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney)
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "NZD",
    }).format(cents / 100);
  }

  properties() {
    const properties = {
      Colour: this.selectedColour()?.value || "",
      "Product option": this.selectedOrderType()?.value || "",
    };
    if (this.customVariantConfiguration) {
      this.customVariantConfiguration.options.forEach((option) => {
        const input = this.section.querySelector(
          `[data-promo-config-option="${CSS.escape(String(option.key))}"]:checked`,
        );
        if (input) properties[option.name] = input.value;
      });
      const variant = this.selectedCustomVariant();
      if (variant?.sku) properties.SKU = String(variant.sku);
    }
    const branding = this.selectedBranding();
    const packaging = this.selectedPackaging();
    if (branding) properties.Branding = branding.value;
    if (packaging) properties.Packaging = packaging.value;
    return properties;
  }

  async createConfigurationVariant({
    colour,
    quantity,
    orderType,
    branding,
    packaging,
  }) {
    const packagingMethodId = packaging?.dataset.packagingMethodId || "";
    const service =
      orderType === 1
        ? "unbranded"
        : packagingMethodId
          ? "packaging"
          : "branding";
    const payload = {
      variantId: colour.dataset.variantId,
      variantSku: colour.dataset.variantSku || "",
      quantity,
      service,
      brandingId: null,
      brandingQuantity: null,
      packagingId: null,
      packagingQuantity: null,
      shippingService: null,
    };

    if (service === "branding" || service === "packaging") {
      payload.brandingId =
        branding?.dataset.brandingMethodId || branding?.dataset.addonId || "";
      payload.brandingQuantity = quantity;
    }

    if (service === "packaging") {
      payload.packagingId = packagingMethodId;
      payload.packagingQuantity = quantity;
    }

    const response = await fetch(this.section.dataset.configurationVariantUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });
    const responseText = await response.text();
    let result = {};
    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch (_error) {
      if (!response.ok)
        throw new Error(
          responseText || "Unable to create the configured product.",
        );
    }
    const responseResult =
      result.data && typeof result.data === "object" ? result.data : result;
    if (!response.ok || result.ok === false || result.success === false) {
      throw new Error(
        result.error ||
          result.message ||
          result.description ||
          "Unable to create the configured product.",
      );
    }

    const returnedId =
      responseResult.numericVariantId ||
      responseResult.variantId ||
      responseResult.variant_id ||
      responseResult.shopifyVariantId ||
      responseResult.variant?.id ||
      result.variantId ||
      result.variant_id ||
      result.shopifyVariantId ||
      result.variant?.id ||
      result.data?.variantId ||
      result.data?.variant_id ||
      result.data?.variant?.id;
    if (!returnedId)
      throw new Error("The configuration API did not return a variant ID.");
    const returnedQuantity = Number.parseInt(
      responseResult.cartQuantity ?? quantity,
      10,
    );
    const excludedPropertyNames = new Set([
      "source sku",
      "service",
      "shipping",
      "shipping charge",
      "branding quantity",
      "requested quantity",
      "branding option",
      "packaging option",
    ]);
    const returnedProperties =
      responseResult.properties && typeof responseResult.properties === "object"
        ? Object.fromEntries(
            Object.entries(responseResult.properties).filter(
              ([name]) =>
                !excludedPropertyNames.has(String(name).trim().toLowerCase()),
            ),
          )
        : {};
    return {
      variantId: String(returnedId).split("/").pop(),
      cartQuantity:
        Number.isFinite(returnedQuantity) && returnedQuantity > 0
          ? returnedQuantity
          : quantity,
      properties: returnedProperties,
    };
  }

  async addItemsToCart(items, sections, isConfiguredOrder) {
    const retryDelays = isConfiguredOrder
      ? [0, 600, 1000, 1600, 2600, 4200]
      : [0];

    for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
      if (retryDelays[attempt]) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, retryDelays[attempt]),
        );
      }

      const response = await fetch(`${this.section.dataset.cartAddUrl}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (response.ok) return result;

      const errorMessage = result.description || result.message || "";
      const isTemporaryAvailabilityError =
        response.status === 422 && /sold out|not available/i.test(errorMessage);
      const hasAnotherAttempt = attempt < retryDelays.length - 1;

      if (
        !isConfiguredOrder ||
        !isTemporaryAvailabilityError ||
        !hasAnotherAttempt
      ) {
        throw new Error(errorMessage || "Unable to add these items.");
      }
    }

    throw new Error(
      "The configured product is not available yet. Please try again.",
    );
  }

  bindForm() {
    this.form?.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (this.isSubmitting) return;
      const quantity = this.quantity();
      const colour = this.selectedColour();
      const orderType = Number(
        this.selectedOrderType()?.dataset.orderType || 1,
      );
      const branding = this.selectedBranding();
      const packaging = this.selectedPackaging();
      const customVariant = this.selectedCustomVariant();

      if (quantity < this.minimumQuantity)
        return this.showError(
          `The minimum order quantity is ${this.minimumQuantity.toLocaleString()}.`,
          "quantity",
        );
      if (!quantity || !colour)
        return this.showError(
          "Please select a colour and enter a valid quantity.",
        );
      if (this.customVariantConfiguration && !customVariant)
        return this.showError("Please select all product options.");
      const selectedStock = this.selectedStock();
      if (
        !this.isIndentedProduct &&
        selectedStock !== null &&
        selectedStock.quantity <= 0
      )
        return this.showError(
          this.section.querySelector("[data-promo-error]")?.dataset
            .outOfStockMessage ||
            "The selected SKU is currently out of stock. Please Get a Quote to contact our team for further assistance.",
          "stock",
        );
      if (
        !this.isIndentedProduct &&
        selectedStock !== null &&
        quantity > selectedStock.quantity
      )
        return this.showError(
          `Only ${selectedStock.quantity.toLocaleString()} units are currently available for SKU ${selectedStock.sku}.`,
          "stock",
        );
      const brandingIsRequired = orderType === 2;
      if (brandingIsRequired && !branding)
        return this.showError("Please select a branding option.");
      if (packaging?.hasAttribute("data-request-quote"))
        return this.showError(
          "Custom Corporate Pack is available by quote only.",
        );
      const mainId = colour.dataset.variantId;
      let items = [{ id: mainId, quantity, properties: this.properties() }];
      if (!this.usesJsonPricing && branding?.dataset.addonId)
        items.push({
          id: branding.dataset.addonId,
          quantity,
          parent_id: mainId,
          properties: { "_Parent variant ID": mainId },
        });
      if (!this.usesJsonPricing && branding?.dataset.setupId) {
        items.push({
          id: branding.dataset.setupId,
          quantity: 1,
          parent_id: mainId,
          properties: {
            "_Parent variant ID": mainId,
            "_Add-on type": "Setup Fee",
          },
        });
      }
      if (
        !this.usesJsonPricing &&
        packaging?.dataset.packagingId &&
        Number(packaging.dataset.addonPrice) > 0
      ) {
        items.push({
          id: packaging.dataset.packagingId,
          quantity,
          parent_id: mainId,
          properties: { "_Parent variant ID": mainId },
        });
      }

      const buttons = [...this.section.querySelectorAll("[data-promo-submit]")];
      this.isSubmitting = true;
      buttons.forEach((button) => {
        button.setAttribute("aria-busy", "true");
        const label = button.querySelector("[data-promo-submit-label]");
        if (label) {
          label.dataset.defaultLabel = label.textContent;
          label.textContent = "Adding…";
        }
        button.disabled = true;
      });
      this.showError("");

      try {
        let isConfiguredOrder = false;
        if (this.usesJsonPricing) {
          const configuredVariant = await this.createConfigurationVariant({
            colour,
            quantity,
            orderType,
            branding,
            packaging,
          });
          const configuredItem = {
            id: configuredVariant.variantId,
            quantity: configuredVariant.cartQuantity,
            properties: configuredVariant.properties,
          };
          items = [configuredItem];
          isConfiguredOrder = true;
        }
        const cart =
          document.querySelector("cart-drawer") ||
          document.querySelector("cart-notification");
        const defaultCartSections = cart?.matches("cart-drawer")
          ? ["cart-drawer", "cart-icon-bubble"]
          : [];
        const sections =
          cart?.getSectionsToRender?.().map((section) => section.id) ||
          defaultCartSections;
        cart?.setActiveElement?.(event.submitter || document.activeElement);
        const result = await this.addItemsToCart(
          items,
          sections,
          isConfiguredOrder,
        );
        if (cart?.renderContents && result.sections) {
          cart.classList.remove("is-empty");
          cart.renderContents(result);
        } else window.location.href = window.routes?.cart_url || "/cart";
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.isSubmitting = false;
        buttons.forEach((button) => {
          button.removeAttribute("aria-busy");
          const label = button.querySelector("[data-promo-submit-label]");
          if (label?.dataset.defaultLabel) {
            label.textContent = label.dataset.defaultLabel;
            delete label.dataset.defaultLabel;
          }
          button.disabled = false;
        });
        this.update();
      }
    });
  }

  showError(message, source = "") {
    const error = this.section.querySelector("[data-promo-error]");
    if (!error) return;
    const emphasis = "Get a Quote";
    const emphasisIndex = message.indexOf(emphasis);
    error.replaceChildren();
    if (source === "stock" && emphasisIndex >= 0) {
      error.append(
        document.createTextNode(message.slice(0, emphasisIndex)),
        Object.assign(document.createElement("strong"), {
          textContent: emphasis,
        }),
        document.createTextNode(message.slice(emphasisIndex + emphasis.length)),
      );
    } else {
      error.textContent = message;
    }
    error.hidden = !message;
    if (message && source) error.dataset.errorSource = source;
    else delete error.dataset.errorSource;
  }

  paintSwatches() {
    const colours = {};
    this.section
      .querySelectorAll("[data-promo-colour-map]")
      .forEach((mappingElement) => {
        let mapping = "";
        try {
          mapping = JSON.parse(mappingElement.textContent || '""');
        } catch (_error) {
          mapping = mappingElement.textContent || "";
        }
        String(mapping)
          .split(/\r?\n/)
          .forEach((line) => {
            const separator = line.search(/[:=]/);
            if (separator < 1) return;
            const name = line.slice(0, separator).trim().toLowerCase();
            const code = line.slice(separator + 1).trim();
            if (name && code) colours[name] = code;
          });
      });
    this.section.querySelectorAll("[data-colour-name]").forEach((swatch) => {
      const colourName = swatch.dataset.colourName.trim().toLowerCase();
      const colourParts = colourName.split(/\s+-\s+/);
      const colourSuffix = colourParts[colourParts.length - 1].trim();
      const colourKeys = Object.keys(colours).sort(
        (first, second) => second.length - first.length,
      );
      const containedColour =
        colourKeys.find((name) => colourSuffix.includes(name)) ||
        colourKeys.find((name) => colourName.includes(name));
      const standardColour = [colourSuffix, colourSuffix.replace(/\s+/g, "")]
        .find((name) => window.CSS?.supports?.("color", name));

      swatch.style.setProperty(
        "--swatch",
        colours[colourName] ||
          colours[colourSuffix] ||
          standardColour ||
          (containedColour ? colours[containedColour] : "") ||
          "#d9d9d9",
      );
    });
  }
}

const initPromotionalProducts = (scope = document) => {
  scope.querySelectorAll("[data-promotional-product]").forEach((section) => {
    if (section.dataset.initialized) return;
    section.dataset.initialized = "true";
    new PromotionalMaterialsProduct(section);
  });
};

document.addEventListener("DOMContentLoaded", () => initPromotionalProducts());
document.addEventListener("shopify:section:load", (event) =>
  initPromotionalProducts(event.target),
);
